/**
 * Harmony Oud — Cart Drawer
 * Slide-in cart using Shopify AJAX Cart API.
 */
(function () {
  'use strict';

  function $id(id) { return document.getElementById(id); }

  var overlay = $id('cart-overlay');
  var drawer = $id('cart-drawer');
  var closeBtn = $id('cart-drawer-close');
  var itemsWrap = $id('cart-items');
  var emptyWrap = $id('cart-empty');
  var footerWrap = $id('cart-footer');
  var subtotalEl = $id('cart-subtotal');
  var statusEl = $id('cart-status');
  var toast = $id('cart-toast');
  var toastMessage = $id('cart-toast-message');
  var toastLink = toast && toast.querySelector('.cart-toast__link');
  var countEls = document.querySelectorAll('.cart-count');
  var pendingKeys = {};
  var lastActiveElement = null;
  var toastTimer = null;

  function translate(key, fallback) {
    return window.HarmonyOud && window.HarmonyOud.t && window.HarmonyOud.t[key]
      ? window.HarmonyOud.t[key]
      : fallback;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function restoreBodyScroll() {
    var mobileMenu = $id('mobile-menu');
    document.body.style.overflow = mobileMenu && mobileMenu.classList.contains('open') ? 'hidden' : '';
  }

  function openCart(trigger) {
    if (trigger) lastActiveElement = trigger;
    else if (!lastActiveElement) lastActiveElement = document.activeElement;
    overlay && overlay.classList.add('open');
    if (drawer) {
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    fetchCart();
    window.requestAnimationFrame(function () {
      if (closeBtn && drawer && drawer.classList.contains('open')) closeBtn.focus();
    });
  }

  function closeCart() {
    overlay && overlay.classList.remove('open');
    if (drawer) {
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    restoreBodyScroll();
    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
    lastActiveElement = null;
  }

  function isCartOpen() {
    return !!(drawer && drawer.classList.contains('open'));
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .then(function (cart) {
        renderCart(cart);
        return cart;
      })
      .catch(function (error) {
        console.error('Cart fetch error', error);
        showStatus(translate('cart_error', 'Something went wrong. Please try again.'));
        throw error;
      });
  }

  function renderCart(cart) {
    updateCount(cart.item_count);

    if (cart.item_count === 0) {
      if (itemsWrap) {
        itemsWrap.innerHTML = '';
        itemsWrap.style.display = 'none';
      }
      if (emptyWrap) emptyWrap.style.display = 'flex';
      if (footerWrap) footerWrap.style.display = 'none';
      return;
    }

    if (emptyWrap) emptyWrap.style.display = 'none';
    if (itemsWrap) itemsWrap.style.display = 'block';
    if (footerWrap) footerWrap.style.display = 'flex';
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (!itemsWrap) return;
    itemsWrap.innerHTML = cart.items.map(renderItem).join('');

    itemsWrap.querySelectorAll('[data-qty-change]').forEach(function (button) {
      button.addEventListener('click', function () {
        var item = button.closest('.cart-item');
        var key = button.dataset.key;
        var delta = parseInt(button.dataset.qtyChange, 10);
        var qtyEl = item && item.querySelector('.cart-item__qty-num');
        var current = parseInt(qtyEl ? qtyEl.textContent : '1', 10);
        updateQty(key, Math.max(0, current + delta), item);
      });
    });

    itemsWrap.querySelectorAll('[data-remove]').forEach(function (button) {
      button.addEventListener('click', function () {
        updateQty(button.dataset.remove, 0, button.closest('.cart-item'));
      });
    });
  }

  function renderItem(item) {
    var title = escapeHtml(item.product_title || item.title);
    var variant = escapeHtml(item.variant_title);
    var key = escapeHtml(item.key);
    var image = item.image
      ? '<img class="cart-item__image" src="' + escapeHtml(item.image) + '" alt="' + title + '" loading="lazy">'
      : '<div class="cart-item__image" style="background:var(--color-surface-variant);"></div>';

    return [
      '<div class="cart-item">',
        image,
        '<div class="cart-item__info">',
          '<div class="cart-item__title">' + title + '</div>',
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<div class="cart-item__variant">' + variant + '</div>'
            : '',
          '<div class="cart-item__price">' + formatMoney(item.final_line_price) + '</div>',
          '<div class="cart-item__actions">',
            '<div class="cart-item__qty">',
              '<button data-key="' + key + '" data-qty-change="-1" aria-label="' + escapeHtml(translate('cart_decrease', 'Decrease quantity')) + '">−</button>',
              '<span class="cart-item__qty-num">' + escapeHtml(item.quantity) + '</span>',
              '<button data-key="' + key + '" data-qty-change="1" aria-label="' + escapeHtml(translate('cart_increase', 'Increase quantity')) + '">+</button>',
            '</div>',
            '<button class="cart-item__remove" data-remove="' + key + '">' +
              escapeHtml(translate('cart_remove', 'Remove')) +
            '</button>',
          '</div>',
        '</div>',
      '</div>'
    ].join('');
  }

  function setItemBusy(item, busy) {
    if (!item) return;
    item.classList.toggle('is-updating', busy);
    item.setAttribute('aria-busy', busy ? 'true' : 'false');
    item.querySelectorAll('button').forEach(function (button) {
      button.disabled = busy;
    });
  }

  function updateQty(key, qty, item) {
    if (!key || pendingKeys[key]) return;
    pendingKeys[key] = true;
    setItemBusy(item, true);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart update failed');
        return response.json();
      })
      .then(function (cart) {
        showStatus('');
        renderCart(cart);
      })
      .catch(function (error) {
        console.error('Cart update error', error);
        setItemBusy(item, false);
        showStatus(translate('cart_error', 'Something went wrong. Please try again.'));
      })
      .then(function () {
        delete pendingKeys[key];
      });
  }

  function updateCount(count) {
    countEls.forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  function showStatus(message) {
    if (statusEl) statusEl.textContent = message || '';
  }

  function showToast(message, showLink) {
    if (!toast || !toastMessage) return;
    toastMessage.textContent = message;
    if (toastLink) toastLink.hidden = showLink === false;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.hidden = true;
    }, 5000);
  }

  function formatMoney(cents) {
    var currency = window.HarmonyOud && window.HarmonyOud.currency
      ? window.HarmonyOud.currency
      : 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  window.HarmonyCart = {
    open: openCart,
    add: function (variantId, qty, properties, options) {
      options = options || {};
      var trigger = options.trigger;
      var shouldOpen = options.open !== false;
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: qty || 1,
          properties: properties || {}
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then(function () {
          return fetchCart();
        })
        .then(function () {
          if (shouldOpen) {
            openCart(trigger);
          } else {
            showToast(translate('cart_added', 'Item added to your cart'), true);
          }
        })
        .catch(function (error) {
          console.error('Add to cart error', error);
          showStatus(translate('cart_error', 'Something went wrong. Please try again.'));
          showToast(translate('cart_error', 'Something went wrong. Please try again.'), false);
        })
        .then(function () {
          if (trigger) {
            trigger.disabled = false;
            trigger.removeAttribute('aria-busy');
          }
        });
    }
  };

  overlay && overlay.addEventListener('click', closeCart);
  closeBtn && closeBtn.addEventListener('click', closeCart);
  document.querySelectorAll('[data-close-cart]').forEach(function (el) {
    el.addEventListener('click', closeCart);
  });
  document.querySelectorAll('[data-open-cart]').forEach(function (el) {
    el.addEventListener('click', function () { openCart(el); });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isCartOpen()) {
      closeCart();
      return;
    }
    if (event.key !== 'Tab' || !isCartOpen()) return;
    var focusables = Array.prototype.slice.call(
      drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  var metaCount = document.querySelector('meta[name="cart-count"]');
  if (metaCount) updateCount(parseInt(metaCount.content, 10));
})();
